import { SELECTORS as DRAWER_SELECTORS } from './drawer';

const SELECTOR = `${DRAWER_SELECTORS.main}--panel--navigation`

export default class MainMenu {
    #context = null
    #element = null
    
    #options = {
        menuWrapper: '.main-menu--site-nav--wrapper',
        menuOpenClass: 'menu--open',
        topMenuListSelector: '.main-menu--site-nav--list',
        linkItemSelector: '.list-item--link',
        expandableMenuItemSelector: '.list-item.has-children',
        listItemExpandedClass: 'list-item--expanded',
        listItemCollapseSelector: '.main-menu--list--contract',
        topMenuHoverEffectsClass: 'has-hover-effects',
        jsAnimateSmallScreenOnly: true,
        nav: SELECTOR
    };

    // Top level menu items, i.e. expandable
    #expandableMenuItems = [];

    #expandedItem = null;

    // Instance of GSAP lib
    #gsap = undefined;

    constructor(context, options = {}) {
        this.#options = {
            ...this.#options,
            ...options,
        };

        import('gsap').then(({ gsap }) => {
            this.#gsap = gsap;
        }).then(() => {
            this.#context = context
            this.#init();
        });
    }

    #init = () => {
        try {
            this.#context.registerNewElement('nav', this.#context.getElementsBySelector(this.#getOption('nav')))
            this.#context.registerNewElement('topList', this.#context.getElementBySelector(this.#getOption('topMenuListSelector')))
            this.#context.registerNewElement('menuWrapper', this.#context.getElementBySelector(this.#getOption('menuWrapper')))
            this.#assignTopLevelMenuItems();

            this.#context.getRegisteredElement('main').classList.add('active');
            
            this.#element = this.#context.getElementBySelector(SELECTOR)
        } catch (error) {
            console.error(
               `Misconfiguration in MainMenu component:
               • ${error.message}`
            );
        }
    }

    openMenu = () => {
        this.#element.style.display = 'block'

        this.#addMenuItemClickEvents();
        
        this.#handleSelection();
        this.toggleHoverEffects();
    }

    closeMenu = () => {
        this.#element.style.display = 'none'

        this.#clearMenuItemClickEvents();
        
        this.#expandedItem = null;
        this.#handleSelection();
        this.toggleHoverEffects();
    }

    /**
     * Toggle nested menu based on available space when drawer is open
     */
    toggleHoverEffects = () => {
        const list = this.#context.getRegisteredElement('topList');
        const hoverEffectClass = this.#getOption('topMenuHoverEffectsClass');

        if (this.#context.isDrawerOpen() && !this.#hasSpaceForExpandedMenu()) {
            list.classList.remove(hoverEffectClass);
            return
        }

        list.classList.add(hoverEffectClass);
    }

    #handleSelection = () => {
        if (this.#expandedItem) {
            this.#expandSecondTier();
        } else {
            this.#closeSecondTier();
        }

        this.#expandableMenuItems.forEach(item => {
            if (this.#expandedItem === item.linkItem) {
                item.listItem.classList.add(this.#getOption('listItemExpandedClass'));
            } else {
                item.listItem.classList.remove(this.#getOption('listItemExpandedClass'));
            }
        });
    }

    /**
     * Animates the expansion of the second tier of the nav menu
     */
    #expandSecondTier = () => {
        this.#gsap.to(this.#context.getRegisteredElement('topList'), {x: '-100%'});
    }

    /**
     * Animate close the expanded second menu tier
     */
    #closeSecondTier = () => {
        this.#gsap.to(this.#context.getRegisteredElement('topList'), {x: 0});
    }

    /**
     * Returns a configuration option from its key
     * 
     * @param {string} key 
     * @returns {string}
     */
    #getOption = key => {
        if (this.#options[key]) {
            return this.#options[key];
        }

        throw new Error(`Option "${key}" does not exist`);
    };

    #assignTopLevelMenuItems = () => {
        this.#expandableMenuItems = Array.from(this.#context.getElementsBySelector(this.#getOption('expandableMenuItemSelector'), this.#context.getRegisteredElement('topList'))).map(element => {
            return {
                listItem: element,
                linkItem: element.querySelector(this.#getOption('linkItemSelector')),
                collapseTrigger: element.querySelector(this.#getOption('listItemCollapseSelector')),
            };
        });
    };

    #menuItemClickEventHandler = event => {
        event.preventDefault();
        this.#expandedItem = event.target;
        this.#handleSelection();
    }

    #menuItemCollapseEventHandler = () => {
        this.#expandedItem = null;
        this.#handleSelection();
    }

    #addMenuItemClickEvents = () => {
        if (!this.#getOption('jsAnimateSmallScreenOnly') || !this.#hasSpaceForExpandedMenu()) {
            this.#expandableMenuItems.forEach(item => {
                item.linkItem.addEventListener('click', this.#menuItemClickEventHandler);
                item.collapseTrigger.addEventListener('click', this.#menuItemCollapseEventHandler);
            });
        }
    }

    #clearMenuItemClickEvents = () => {
        this.#expandableMenuItems.forEach(item => {
            item.linkItem.removeEventListener('click', this.#menuItemClickEventHandler);
            item.collapseTrigger.removeEventListener('click', this.#menuItemCollapseEventHandler);
        });
    }

    #hasSpaceForExpandedMenu = () => {
        const wrapperWidth = this.#context.getRegisteredElement('menuWrapper').offsetWidth;
        const menuWidth = this.#context.getRegisteredElement('topList').offsetWidth;

        return wrapperWidth >= (menuWidth * 2);
    }
}